import { tWithKeyAttr as t } from "../i18nHelper";
import { CreditType, FeatureOption } from "../services/api/featureCreditPackagesApi";

// Display label for a feature option in the package create/edit select.
// Derived from creditType: token → "AI Tokens"; seat → the patient/therapist
// label (which seat differs by featureKey). Anything else falls back to the
// generic creditType label.
export const featureOptionLabel = (option: FeatureOption): string => {
  if (option.creditType === "token") return t("FEATURE_OPTION_AI_TOKENS");
  if (option.creditType === "seat") {
    switch (option.featureKey) {
      case "MAX_PATIENTS":
        return t("FEATURE_OPTION_PATIENT_SEATS");
      case "MAX_THERAPISTS":
        return t("FEATURE_OPTION_THERAPIST_SEATS");
      default:
        return creditTypeLabel("seat");
    }
  }
  return creditTypeLabel(option.creditType);
};

// Human-readable label for the creditType badge.
export const creditTypeLabel = (creditType: CreditType): string => {
  switch (creditType) {
    case "token":
      return t("CREDIT_TYPE_TOKEN");
    case "seat":
      return t("CREDIT_TYPE_SEAT");
    case "count":
      return t("CREDIT_TYPE_COUNT");
    default:
      return creditType;
  }
};

// Suggested unit for the "amount" field, by creditType.
export const amountUnitLabel = (creditType?: CreditType | null): string => {
  switch (creditType) {
    case "token":
      return t("AMOUNT_UNIT_TOKENS");
    case "seat":
      return t("AMOUNT_UNIT_SEATS");
    case "count":
      return t("AMOUNT_UNIT_USES");
    default:
      return "";
  }
};

const getServerErrorCode = (error: unknown): string | undefined =>
  error && typeof error === "object" && "data" in error
    ? (error as { data?: { error?: string } }).data?.error
    : undefined;

const getErrorStatus = (error: unknown): number | undefined =>
  error && typeof error === "object" && "status" in error
    ? (error as { status?: number }).status
    : undefined;

// Map the server's error codes to friendly messages. Falls back to a generic
// message when the shape is unknown (e.g. network error / 5xx).
export const mapPackageError = (error: unknown): string => {
  switch (getServerErrorCode(error)) {
    case "unknown_feature_key":
      return t("UNKNOWN_FEATURE_KEY_ERROR");
    case "duplicate_currency":
      return t("DUPLICATE_CURRENCY_ERROR");
    case "out_of_scope":
      return t("OUT_OF_SCOPE_ERROR");
    default:
      return t("SOMETHING_WENT_WRONG_TRY_AGAIN");
  }
};

// Map errors from the Buy-credits flow (FeatureCredits/Buy) to friendly messages.
export const mapBuyError = (error: unknown): string => {
  switch (getServerErrorCode(error)) {
    case "out_of_scope":
      return t("OUT_OF_SCOPE_ERROR");
    case "no_price_for_currency":
      return t("BUY_NO_PRICE_FOR_CURRENCY_ERROR");
    case "invalid_scope_for_target":
      // Defensive only: the UI always sends scope "company" alongside
      // targetUserId, so this shouldn't fire from a correct client build.
      return t("BUY_INVALID_SCOPE_FOR_TARGET_ERROR");
    case "no_amount_due":
      return t("BUY_SESSION_FAILED_ERROR");
    default:
      return getErrorStatus(error) === 403
        ? t("BUY_NOT_ALLOWED_ERROR")
        : t("SOMETHING_WENT_WRONG_TRY_AGAIN");
  }
};
